import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  QUICK_ACTION_RECIPES,
  DEFAULT_LIGHT_EXTEND_OPTIONS,
  DEFAULT_RUN_NOW_OPTIONS,
  getPresetRecipeDefinition,
  getPresetRecipeOptions,
  isExtendLightsPreset,
} from '../../utils/quickActionUtils';

function sensorLabelForAction(action, cards) {
  const sensor = cards.find((card) => card.macAddress === action.sensor);
  return sensor?.sensorDescription || action.sensor || 'Unpaired';
}

export default function QuickActionPresetForm({
  actions,
  cards,
  preset,
  onSave,
  onCancel,
}) {
  const [name, setName] = useState('');
  const [recipe, setRecipe] = useState(QUICK_ACTION_RECIPES.EXTEND_LIGHTS);
  const [endTime, setEndTime] = useState(DEFAULT_LIGHT_EXTEND_OPTIONS.endTime);
  const [timezone, setTimezone] = useState(DEFAULT_LIGHT_EXTEND_OPTIONS.timezone);
  const [duration, setDuration] = useState(DEFAULT_RUN_NOW_OPTIONS.duration);
  const [sensorTracking, setSensorTracking] = useState(DEFAULT_RUN_NOW_OPTIONS.sensorTracking);
  const [selectedMacs, setSelectedMacs] = useState([]);

  const recipeOptions = useMemo(
    () => getPresetRecipeOptions(preset?.recipe),
    [preset?.recipe]
  );

  const recipeDefinition = useMemo(
    () => getPresetRecipeDefinition(recipe),
    [recipe]
  );

  useEffect(() => {
    setName(preset?.name || '');
    setRecipe(preset?.recipe || QUICK_ACTION_RECIPES.EXTEND_LIGHTS);
    setEndTime(preset?.options?.endTime || DEFAULT_LIGHT_EXTEND_OPTIONS.endTime);
    setTimezone(preset?.options?.timezone || DEFAULT_LIGHT_EXTEND_OPTIONS.timezone);
    setDuration(preset?.options?.duration ?? DEFAULT_RUN_NOW_OPTIONS.duration);
    setSensorTracking(preset?.options?.sensorTracking ?? DEFAULT_RUN_NOW_OPTIONS.sensorTracking);
    setSelectedMacs(preset?.actionMacs || []);
  }, [preset]);

  const availableActions = useMemo(
    () => actions.filter((action) => recipeDefinition.actuatorTypes.includes(action.actuatorType)),
    [actions, recipeDefinition.actuatorTypes]
  );

  const groupedActions = useMemo(() => {
    return availableActions.reduce((groups, action) => {
      const label = sensorLabelForAction(action, cards);
      if (!groups[label]) groups[label] = [];
      groups[label].push(action);
      return groups;
    }, {});
  }, [availableActions, cards]);

  const toggleAction = (macAddress) => {
    setSelectedMacs((current) =>
      current.includes(macAddress)
        ? current.filter((item) => item !== macAddress)
        : [...current, macAddress]
    );
  };

  const handleRecipeChange = (nextRecipe) => {
    setRecipe(nextRecipe);
    const nextDefinition = getPresetRecipeDefinition(nextRecipe);
    setSelectedMacs((current) =>
      current.filter((mac) => {
        const action = actions.find((item) => item.macAddress === mac);
        return action && nextDefinition.actuatorTypes.includes(action.actuatorType);
      })
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (selectedMacs.length === 0) return;

    onSave({
      ...preset,
      name,
      recipe,
      actionMacs: selectedMacs,
      options: isExtendLightsPreset(recipe)
        ? { endTime, timezone }
        : {
            duration: Number(duration) || DEFAULT_RUN_NOW_OPTIONS.duration,
            sensorTracking,
          },
    });
  };

  return (
    <form className="quick-action-form" onSubmit={handleSubmit}>
      <header className="quick-action-form-header">
        <h2 className="quick-action-modal-title">{preset ? 'Edit preset' : 'Create preset'}</h2>
        <p className="quick-action-modal-desc">
          Choose a recipe for lights, irrigation, fans, or humidifiers, then pick the devices to include.
        </p>
      </header>

      <label className="quick-action-field">
        <span>Preset name</span>
        <input
          type="text"
          value={name}
          placeholder={recipeDefinition.placeholder}
          onChange={(event) => setName(event.target.value)}
          required
        />
      </label>

      <label className="quick-action-field">
        <span>Recipe</span>
        <select value={recipe} onChange={(event) => handleRecipeChange(event.target.value)}>
          {recipeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {isExtendLightsPreset(recipe) && (
        <div className="quick-action-run-settings">
          <h3 className="quick-action-picker-title">Light schedule</h3>
          <div className="quick-action-form-grid">
            <label className="quick-action-field">
              <span>Extend until</span>
              <input
                type="time"
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
              />
            </label>
            <label className="quick-action-field">
              <span>Timezone</span>
              <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
                <option value="UTC">UTC</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/New_York">Eastern Time</option>
              </select>
            </label>
          </div>
        </div>
      )}

      {recipeDefinition.hasIrrigationOptions && (
        <div className="quick-action-run-settings">
          <h3 className="quick-action-picker-title">Irrigation settings</h3>
          <div className="quick-action-form-grid">
            <label className="quick-action-field">
              <span>Duration (seconds)</span>
              <input
                type="number"
                min="1"
                max="32"
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
              />
            </label>
            <label className="quick-action-field quick-action-checkbox-field">
              <span>Use sensor data</span>
              <input
                type="checkbox"
                checked={sensorTracking}
                onChange={(event) => setSensorTracking(event.target.checked)}
              />
              <small>When off, irrigation follows the time-based schedule only.</small>
            </label>
          </div>
        </div>
      )}

      {recipeDefinition.value === QUICK_ACTION_RECIPES.RUN_NOW && (
        <div className="quick-action-run-settings">
          <h3 className="quick-action-picker-title">Run settings</h3>
          <p className="quick-action-run-settings-desc">
            Mixed presets can include irrigation, fans, and humidifiers. Irrigation uses the settings below; fans and humidifiers use an immediate 2-second trigger.
          </p>
          <div className="quick-action-form-grid">
            <label className="quick-action-field">
              <span>Irrigation duration (seconds)</span>
              <input
                type="number"
                min="1"
                max="32"
                value={duration}
                onChange={(event) => setDuration(event.target.value)}
              />
            </label>
            <label className="quick-action-field quick-action-checkbox-field">
              <span>Use sensor data for irrigation</span>
              <input
                type="checkbox"
                checked={sensorTracking}
                onChange={(event) => setSensorTracking(event.target.checked)}
              />
              <small>When off, irrigation follows the time-based schedule only.</small>
            </label>
          </div>
        </div>
      )}

      {(recipeDefinition.value === 'run_fan' || recipeDefinition.value === 'run_humidity') && (
        <p className="quick-action-run-settings-desc">
          Runs an immediate 2-second trigger on each selected device.
        </p>
      )}

      <div className="quick-action-picker">
        <h3 className="quick-action-picker-title">Actions</h3>
        {Object.keys(groupedActions).length === 0 && (
          <p className="quick-action-empty">No compatible actions found for this recipe.</p>
        )}
        {Object.entries(groupedActions).map(([groupName, groupActions]) => (
          <div key={groupName} className="quick-action-action-group">
            <p className="quick-action-action-group-title">{groupName}</p>
            {groupActions.map((action) => (
              <label key={action.macAddress} className="quick-action-checkbox">
                <input
                  type="checkbox"
                  checked={selectedMacs.includes(action.macAddress)}
                  onChange={() => toggleAction(action.macAddress)}
                />
                <span>
                  <strong>{action.actuatorDescription || action.macAddress}</strong>
                  <small>{action.actuatorType} · {action.macAddress}</small>
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>

      <footer className="quick-action-form-footer">
        <button type="button" className="quick-action-secondary-btn" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="quick-action-primary-btn"
          disabled={!name.trim() || selectedMacs.length === 0}
        >
          Save preset
        </button>
      </footer>
    </form>
  );
}

QuickActionPresetForm.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.object).isRequired,
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  preset: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
